import styled from "@emotion/styled";
import { theme } from "../lib/theme";

const Svg = styled.svg`
  display: block;
  width: 100%;
`;

export interface SparklineReading {
  /** Position in the source series — a slot offset, never a compacted index. */
  i: number;
  v: number;
}

export interface SparklineGeometry {
  /** Every reading in order: the single path the eye follows across the window. */
  points: SparklineReading[];
  /** Contiguous stretches of readings — measured, so they draw solid. */
  runs: SparklineReading[][];
  /** Connectors spanning a gap between two runs — inferred, so they draw dashed. */
  bridges: [SparklineReading, SparklineReading][];
}

/**
 * Split a gappy series into measured stretches and the connectors that bridge the gaps.
 *
 * Kept separate from the drawing so the one property that matters can be tested without a DOM:
 * a reading stays at its own index, and consecutive readings are always joined — even across a
 * gap — so the series reads as one line rather than as debris.
 */
export function sparklineGeometry(data: (number | null)[]): SparklineGeometry {
  const points: SparklineReading[] = [];
  data.forEach((v, i) => {
    if (v != null) points.push({ i, v });
  });

  const runs: SparklineReading[][] = [];
  const bridges: [SparklineReading, SparklineReading][] = [];
  for (const p of points) {
    const run = runs[runs.length - 1];
    if (run != null && p.i === run[run.length - 1].i + 1) {
      run.push(p);
      continue;
    }
    if (run != null) bridges.push([run[run.length - 1], p]);
    runs.push([p]);
  }

  return { points, runs, bridges };
}

/**
 * Sparkline that keeps each value at its own index.
 *
 * It used to filter nulls out and re-index the survivors, so a gap didn't render as a gap — it
 * compressed the series and shifted everything after it leftwards. That was invisible while the
 * series was a standalone rolling window, but these are now drawn directly beneath the epoch
 * timeline on the same slot positions, so a shifted point would sit under the wrong slot and
 * invite exactly the wrong reading.
 *
 * Gaps therefore keep their width — but they no longer break the line. Cutting the series into
 * one polyline per contiguous run made a metric that is only recorded on slots that produced
 * (block time needs two consecutive blocks; volumes need a block at all) render as a field of
 * disconnected stubs and dots, with no line to follow across the window. The gaps are real and
 * worth seeing, so the fix is to draw them as gaps in a continuous series rather than as absent
 * series: measured stretches are solid, the connector across a gap is dashed and dimmed, and the
 * area fill runs unbroken beneath the whole thing. Nothing is invented — a dashed span states
 * that its endpoints were measured and its middle was not.
 *
 * The y-axis is zero-based, not min-max. Every metric drawn here is a non-negative magnitude
 * (seconds, transactions, mana) where zero is meaningful, and normalising to the data's own
 * minimum made the floor of the box mean "the smallest value present" instead. A perfectly
 * constant series — 64 slots all at exactly 72s, the healthiest block time possible — mapped its
 * only value to the minimum and drew a flat line hugging the bottom edge, visually identical to
 * flatlining at zero. Add two ordinary outliers (a doubled gap after a missed slot) and
 * range-normalisation pinned everything else to the floor while those two filled the box.
 */
export function Sparkline({
  data,
  color = theme.colors.primary,
  height = 48,
}: {
  data: (number | null)[];
  color?: string;
  height?: number;
}) {
  const { points, runs, bridges } = sparklineGeometry(data);
  if (points.length === 0) return null;

  // Zero-based: the floor of the box is 0, so height is proportional to the value itself. An
  // all-zero series still needs a non-zero divisor, and a negative value would escape the box —
  // neither happens with the magnitudes drawn here, but the clamp keeps it honest if one appears.
  //
  // Scaled to the peak plus 10% headroom rather than to the peak itself. With no headroom a
  // constant series — every slot at exactly 72s — pins its only value to the frame's top edge and
  // the area fill below it becomes a solid block, which reads as a rendering fault rather than as
  // steady cadence. The gap above the peak is what makes it legible as a line.
  const max = Math.max(...points.map((p) => p.v), 0);
  const range = max * 1.1 || 1;
  const w = 100;
  // Guard the single-column case so x stays finite.
  const span = Math.max(data.length - 1, 1);

  const x = (i: number) => (i / span) * w;
  const y = (v: number) => height - (Math.max(v, 0) / range) * (height - 4) - 2;
  const at = (p: SparklineReading) => `${x(p.i)},${y(p.v)}`;

  const first = points[0];
  const last = points[points.length - 1];

  return (
    <Svg viewBox={`0 0 ${w} ${height}`} preserveAspectRatio="none" style={{ height }}>
      {/* One fill for the whole series, bridges included. Filling only the solid runs left a
          sparse series as a row of unrelated slivers; the unbroken silhouette is what says
          "one metric over one window". */}
      <polyline
        points={`${x(first.i)},${height} ${points.map(at).join(" ")} ${x(last.i)},${height}`}
        fill={color}
        fillOpacity={0.1}
        stroke="none"
      />
      {/* Non-scaling strokes throughout: the viewBox is stretched horizontally to the container
          (preserveAspectRatio="none"), which would otherwise thicken verticals against
          horizontals and squash the dash pattern into a smear. */}
      {bridges.map(([from, to]) => (
        <line
          key={`bridge-${from.i}`}
          x1={x(from.i)}
          y1={y(from.v)}
          x2={x(to.i)}
          y2={y(to.v)}
          stroke={color}
          strokeOpacity={0.45}
          strokeWidth="1.5"
          strokeDasharray="2 2"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {runs
        .filter((run) => run.length > 1)
        .map((run) => (
          <polyline
            key={`run-${run[0].i}`}
            points={run.map(at).join(" ")}
            fill="none"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      {/* A window holding exactly one reading has no run to draw and nothing to bridge to. Every
          other lone reading is already a vertex of the bridged line.

          Drawn as a zero-length round-capped stroke rather than a <circle>: radii live in user
          units, which the horizontal stretch turns into a flat ellipse, while a non-scaling stroke
          is measured in screen pixels and stays a dot. */}
      {points.length === 1 && (
        <line
          x1={x(first.i)}
          y1={y(first.v)}
          x2={x(first.i)}
          y2={y(first.v)}
          stroke={color}
          strokeWidth="3"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      )}
    </Svg>
  );
}
